import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { Date as DayLabel } from "@dashboard/components/Date/Date";
import { ListPageLayout } from "@dashboard/components/Layouts/List/Root";
import { ResponsiveTable } from "@dashboard/components/ResponsiveTable/ResponsiveTable";
import { TablePagination } from "@dashboard/components/TablePagination/TablePagination";
import TableRowLink from "@dashboard/components/TableRowLink/TableRowLink";
import { type AtticApiKeyFragment, AtticApiKeyStatusEnum } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { sectionNames } from "@dashboard/intl";
import { renderCollection } from "@dashboard/misc";
import { TableBody, TableCell, TableFooter, TableHead } from "@material-ui/core";
import { Box, Button, Skeleton, Text, Tooltip } from "@saleor/macaw-ui-next";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { listMessages, sectionMessages } from "../../messages";
import { atticApiKeyAddPath, atticApiKeyUrl } from "../../urls";
import { expiryIsoToDay, isStaleApiKey } from "../../utils";
import AtticApiKeyStatusPill, {
  AtticApiKeyReadOnlyPill,
} from "../AtticApiKeyStatusPill/AtticApiKeyStatusPill";

const numberOfColumns = 6;
/** Up to this many permission names are listed; more are counted. */
const PERMISSIONS_LISTED = 2;

const PermissionsSummary = ({ permissions }: Pick<AtticApiKeyFragment, "permissions">) => {
  const intl = useIntl();

  if (permissions.length === 0) {
    return <Text size={3}>{intl.formatMessage(listMessages.noPermissions)}</Text>;
  }

  if (permissions.length > PERMISSIONS_LISTED) {
    return (
      <Text size={3} title={permissions.map(permission => permission.name).join(", ")}>
        {intl.formatMessage(listMessages.permissionCount, { count: permissions.length })}
      </Text>
    );
  }

  return <Text size={3}>{permissions.map(permission => permission.name).join(", ")}</Text>;
};

/** Last used, with the 90-day flag on a key nobody seems to use any more. */
export const AtticApiKeyLastUsed = ({
  apiKey,
  now,
}: {
  apiKey: Pick<AtticApiKeyFragment, "status" | "lastUsedOn" | "createdAt">;
  now: number;
}) => {
  const intl = useIntl();
  const stale = isStaleApiKey(apiKey, now);

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Text size={3}>
        {apiKey.lastUsedOn ? (
          <DayLabel date={apiKey.lastUsedOn} plain />
        ) : (
          intl.formatMessage(listMessages.never)
        )}
      </Text>
      {stale && (
        <Tooltip>
          <Tooltip.Trigger>
            <Text size={2} color="warning1" data-test-id="attic-api-key-stale">
              {intl.formatMessage(listMessages.stale)}
            </Text>
          </Tooltip.Trigger>
          <Tooltip.Content side="bottom">
            <Tooltip.Arrow />
            {intl.formatMessage(listMessages.staleHint)}
          </Tooltip.Content>
        </Tooltip>
      )}
    </Box>
  );
};

export interface AtticApiKeyListPageProps {
  /** Undefined while loading, which paints skeleton rows. */
  apiKeys: AtticApiKeyFragment[] | undefined;
  loadFailed: boolean;
  /** The clock the 90-day flag reads; a prop so a test can fix it. */
  now: number;
  rowNumber: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onRowNumberChange: (rowNumber: number) => void;
  onNextPage: () => void;
  onPreviousPage: () => void;
}

export const AtticApiKeyListPage = ({
  apiKeys,
  loadFailed,
  now,
  rowNumber,
  hasNextPage,
  hasPreviousPage,
  onRowNumberChange,
  onNextPage,
  onPreviousPage,
}: AtticApiKeyListPageProps) => {
  const intl = useIntl();
  const navigate = useNavigator();

  return (
    <ListPageLayout>
      <TopNav title={intl.formatMessage(sectionNames.atticApiKeys)}>
        <Button
          variant="primary"
          onClick={() => navigate(atticApiKeyAddPath)}
          data-test-id="attic-api-key-create"
        >
          {intl.formatMessage(listMessages.createKey)}
        </Button>
      </TopNav>
      <DashboardCard data-test-id="attic-api-key-list">
        <DashboardCard.Content paddingX={0}>
          {loadFailed ? (
            <Box paddingX={6} paddingY={4}>
              <Text size={3} color="critical1" data-test-id="attic-api-key-load-failed">
                {intl.formatMessage(sectionMessages.loadFailed)}
              </Text>
            </Box>
          ) : (
            <ResponsiveTable>
              <TableHead>
                <TableRowLink>
                  <TableCell>{intl.formatMessage(listMessages.label)}</TableCell>
                  <TableCell>{intl.formatMessage(listMessages.status)}</TableCell>
                  <TableCell>{intl.formatMessage(listMessages.permissions)}</TableCell>
                  <TableCell>{intl.formatMessage(listMessages.lastUsed)}</TableCell>
                  <TableCell>{intl.formatMessage(listMessages.expires)}</TableCell>
                  <TableCell>{intl.formatMessage(listMessages.created)}</TableCell>
                </TableRowLink>
              </TableHead>
              <TableBody>
                {renderCollection(
                  apiKeys,
                  apiKey => (
                    <TableRowLink
                      key={apiKey ? apiKey.id : "skeleton"}
                      hover={!!apiKey}
                      onClick={apiKey ? () => navigate(atticApiKeyUrl(apiKey.id)) : undefined}
                      data-test-id="attic-api-key-row"
                    >
                      <TableCell data-test-id="attic-api-key-row-label">
                        {apiKey ? (
                          // A real link as well as a clickable row, so the key
                          // opens in a new tab like any other list row.
                          <Link
                            to={atticApiKeyUrl(apiKey.id)}
                            onClick={event => event.stopPropagation()}
                          >
                            <Text size={3} fontWeight="medium">
                              {apiKey.label}
                            </Text>
                          </Link>
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                      <TableCell>
                        {apiKey ? (
                          <Box display="flex" gap={1} flexWrap="wrap">
                            <AtticApiKeyStatusPill status={apiKey.status} />
                            {/* Not beside "Revoked": that key does nothing at all. */}
                            {apiKey.readOnly && apiKey.status !== AtticApiKeyStatusEnum.REVOKED && (
                              <AtticApiKeyReadOnlyPill />
                            )}
                          </Box>
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                      <TableCell data-test-id="attic-api-key-row-permissions">
                        {apiKey ? (
                          <PermissionsSummary permissions={apiKey.permissions} />
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                      <TableCell data-test-id="attic-api-key-row-last-used">
                        {apiKey ? <AtticApiKeyLastUsed apiKey={apiKey} now={now} /> : <Skeleton />}
                      </TableCell>
                      <TableCell data-test-id="attic-api-key-row-expires">
                        {apiKey ? (
                          <Text size={3}>
                            {apiKey.expiresAt ? (
                              <DayLabel date={expiryIsoToDay(apiKey.expiresAt)} plain />
                            ) : (
                              intl.formatMessage(listMessages.never)
                            )}
                          </Text>
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                      <TableCell>
                        {apiKey ? (
                          <Text size={3}>
                            <DayLabel date={apiKey.createdAt} plain />
                          </Text>
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                    </TableRowLink>
                  ),
                  () => (
                    <TableRowLink data-test-id="attic-api-key-list-empty">
                      <TableCell colSpan={numberOfColumns}>
                        <Text size={3} color="default2">
                          {intl.formatMessage(listMessages.empty)}
                        </Text>
                      </TableCell>
                    </TableRowLink>
                  ),
                )}
              </TableBody>
              <TableFooter>
                <TableRowLink>
                  <TablePagination
                    colSpan={numberOfColumns}
                    settings={{ rowNumber }}
                    onUpdateListSettings={(_, value) => onRowNumberChange(value as number)}
                    hasNextPage={hasNextPage}
                    hasPreviousPage={hasPreviousPage}
                    onNextPage={onNextPage}
                    onPreviousPage={onPreviousPage}
                  />
                </TableRowLink>
              </TableFooter>
            </ResponsiveTable>
          )}
        </DashboardCard.Content>
      </DashboardCard>
    </ListPageLayout>
  );
};

export default AtticApiKeyListPage;
